import { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  useCollectionBinding,
  useProperty,
  useSlotGroup,
  EditableSlotGroup,
  EditableCollectionBinding,
} from '@vibesites/runtime/react';

interface PropertyPin {
  name: string;
  address: string;
  lat: number;
  lng: number;
}

// Dual-community property pins (Fox Ridge + Lakeview Park). Matches the
// Footer's dual leasing-office addresses — this site markets two communities
// under one property record, so the neighborhood map needs a pin for each.
const foxRidgePinDef: SlotGroupDefinition = {
  label: 'Fox Ridge Pin',
  fields: {
    name: { label: 'Community Name', type: 'text', defaultValue: 'Fox Ridge Apartments' },
    address: {
      label: 'Address',
      type: 'text',
      defaultField: 'property_full_address',
      defaultValue: '817 Walden Ct, Limerick, PA 19468',
    },
    latitude: {
      label: 'Latitude',
      type: 'text',
      defaultField: 'property_address_latitude',
      defaultValue: '40.2385398',
    },
    longitude: {
      label: 'Longitude',
      type: 'text',
      defaultField: 'property_address_longitude',
      defaultValue: '-75.5371723',
    },
  },
};

const lakeviewParkPinDef: SlotGroupDefinition = {
  label: 'Lakeview Park Pin',
  fields: {
    name: { label: 'Community Name', type: 'text', defaultValue: 'Lakeview Park Apartments' },
    address: {
      label: 'Address',
      type: 'text',
      defaultValue: '601 Lakeview Dr, Royersford, PA 19468',
    },
    latitude: { label: 'Latitude', type: 'text', defaultValue: '40.1965706' },
    longitude: { label: 'Longitude', type: 'text', defaultValue: '-75.5315838' },
  },
};

interface POI {
  poi_name?: string;
  poi_type?: string;
  poi_address?: string;
  poi_short_description?: string;
  poi_long_description?: string;
  poi_featured_image?: string;
  poi_business_url?: string;
  poi_business_phone?: string;
  poi_directions_map_url?: string;
  poi_miles_from_property?: string;
  poi_walking_minutes?: string;
  poi_driving_minutes?: string;
  poi_biking_minutes?: string;
  poi_latitude?: string;
  poi_longitude?: string;
  poi_map_icon?: string;
  poi_google_place_id?: string;
  poi_place_primary_type?: string;
}

function hasVal(v: string | null | undefined): v is string {
  return !!v && v !== 'false' && v !== 'null' && v !== 'undefined' && v.trim() !== '';
}

// Popup addresses look tidier without the country tail — the map is already
// implicitly US-only for these properties, so ", USA" (or " USA") at the end
// is just noise. Strip once and reuse for both the POI and property popups.
function stripCountry(address: string): string {
  return address.replace(/,?\s*USA\.?\s*$/i, '').trim();
}

// Split "Street, City, ST ZIP" so long popup addresses break before the
// locality instead of wrapping mid-zip. Returns [street, locality] when the
// US trailing pattern matches; otherwise a single-line array.
function splitAddressLines(address: string): [string, string] | [string] {
  const clean = stripCountry(address);
  if (!clean) return [''];
  // Street, City, ST ZIP
  const withZip = clean.match(/^(.+),\s*([^,]+,\s*[A-Z]{2}\s+\d{5}(?:-\d{4})?)$/i);
  if (withZip) return [withZip[1].trim(), withZip[2].trim()];
  // Street, City, ST, ZIP
  const zipComma = clean.match(/^(.+),\s*([^,]+,\s*[A-Z]{2}),\s*(\d{5}(?:-\d{4})?)$/i);
  if (zipComma) return [zipComma[1].trim(), `${zipComma[2].trim()} ${zipComma[3]}`];
  return [clean];
}

function appendPopupAddress(parent: HTMLElement, address: string): void {
  const lines = splitAddressLines(address);
  if (!lines[0]) return;
  const addr = document.createElement('div');
  addr.style.cssText = 'font-size:12px;color:#666;margin-top:3px;line-height:1.35;';
  if (lines.length === 2) {
    const street = document.createElement('div');
    street.textContent = lines[0];
    const locality = document.createElement('div');
    locality.textContent = lines[1];
    addr.appendChild(street);
    addr.appendChild(locality);
  } else {
    addr.textContent = lines[0];
  }
  parent.appendChild(addr);
}

// ============================================================================
// Map styling & markers
// ============================================================================

const MAPS_API_KEY =
  (typeof import.meta !== 'undefined' && (import.meta as any).env?.GOOGLE_MAPS_API_KEY) || '';

let mapsLoaderPromise: Promise<void> | null = null;

function loadGoogleMaps(): Promise<void> {
  if ((window as any).google?.maps?.Map) return Promise.resolve();
  if (mapsLoaderPromise) return mapsLoaderPromise;
  mapsLoaderPromise = new Promise<void>((resolve, reject) => {
    if (!MAPS_API_KEY) { reject(new Error('No key')); return; }
    const cb = `__gmapsInit_${Date.now()}`;
    (window as any)[cb] = () => { delete (window as any)[cb]; resolve(); };
    const s = document.createElement('script');
    s.src = `https://maps.googleapis.com/maps/api/js?key=${MAPS_API_KEY}&callback=${cb}`;
    s.onerror = () => { mapsLoaderPromise = null; reject(new Error('Script failed')); };
    document.head.appendChild(s);
  });
  return mapsLoaderPromise;
}

function makeSvgMarker(fillColor: string, glyphSvg: string, size = 30): string {
  return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(`
<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${Math.round(size * 1.3)}" viewBox="0 0 30 39">
  <path d="M15 0C6.72 0 0 6.72 0 15c0 11.25 15 24 15 24s15-12.75 15-24C30 6.72 23.28 0 15 0z" fill="${fillColor}" stroke="#fff" stroke-width="1.5"/>
  <g transform="translate(7,7)" fill="#fff"><g transform="translate(8,8) scale(0.55) translate(-8,-8)">${glyphSvg}</g></g>
</svg>`)}`;
}

function makeHighlightMarker(fillColor: string, glyphSvg: string, size = 40): string {
  return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(`
<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${Math.round(size * 1.35)}" viewBox="0 0 40 54">
  <ellipse cx="20" cy="51" rx="8" ry="3" fill="rgba(0,0,0,0.2)"/>
  <path d="M20 0C9 0 0 9 0 20c0 15 20 31 20 31s20-16 20-31C40 9 31 0 20 0z" fill="${fillColor}" stroke="#fff" stroke-width="2"/>
  <g transform="translate(10,10)" fill="#fff"><g transform="translate(10,10) scale(0.7) translate(-8,-8)">${glyphSvg}</g></g>
</svg>`)}`;
}

// Whole pin body is the theme's surface color — no contrasting inner disc, so
// the pin reads as a single solid shape. The house glyph stays white for
// legibility on the surface fill. No outer stroke: it read as a bright white
// halo against the muted map underneath, and pushed the top of the teardrop
// outside the SVG bounds (clipping the crown). `primaryColor` is unused for
// the standalone (no-icon) case but kept in the signature since the icon-case
// still relies on the surface fill for the icon backdrop.
function makePropertyPinBg(surfaceColor: string, _primaryColor: string, hasIcon: boolean): string {
  const inner = hasIcon
    // Icon backdrop matches the teardrop so the pin looks uniform. The custom
    // icon sits on top as a separate marker.
    ? `<circle cx="30" cy="27" r="18" fill="${surfaceColor}"/>`
    : `<path d="M24 26v10h4v-5h4v5h4V26l-6-5z" fill="#fff"/>
       <path d="M21 27l9-8 9 8" fill="none" stroke="#fff" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>`;
  return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(`
<svg xmlns="http://www.w3.org/2000/svg" width="60" height="74" viewBox="0 0 60 74">
  <ellipse cx="30" cy="70" rx="12" ry="4" fill="rgba(0,0,0,0.2)"/>
  <path d="M30 0C13.43 0 0 13.43 0 30c0 22.5 30 42 30 42s30-19.5 30-42C60 13.43 46.57 0 30 0z" fill="${surfaceColor}"/>
  ${inner}
</svg>`)}`;
}

const CATEGORY_ICONS: Record<string, { glyph: string; hue: number }> = {
  restaurant: { glyph: '<path d="M2 0v6h2v10h2V6h2V0H6V4H4V0zm8 0v8h4V6h2v10h2V0z" transform="scale(0.9)"/>', hue: 0 },
  shopping: { glyph: '<path d="M2 2v2H0v10h16V4h-2V2a4 4 0 00-8 0v0zm2 0a2 2 0 014 0v2H4V2z" transform="scale(0.95)"/>', hue: 270 },
  education: { glyph: '<path d="M8 0L0 4l8 4 8-4-8-4zM2 6v4l6 3 6-3V6L8 9 2 6z"/>', hue: 200 },
  health: { glyph: '<path d="M6 0v6H0v4h6v6h4v-6h6V6h-6V0z" transform="scale(0.95)"/>', hue: 350 },
  fitness: { glyph: '<path d="M6 0v6H0v4h6v6h4v-6h6V6h-6V0z" transform="scale(0.95)"/>', hue: 350 },
  entertainment: { glyph: '<path d="M8 0a8 8 0 100 16A8 8 0 008 0zm0 2a6 6 0 110 12A6 6 0 018 2zm-2 3v6l5-3-5-3z" transform="scale(0.95)"/>', hue: 45 },
  park: { glyph: '<path d="M8 0L4 6h2L2 12h12L10 6h2L8 0zM7 12v4h2v-4z" transform="scale(0.95)"/>', hue: 140 },
  transit: { glyph: '<path d="M4 0C2 0 0 1 0 3v7a3 3 0 003 3l-1 2v1h2l1-2h6l1 2h2v-1l-1-2a3 3 0 003-3V3c0-2-2-3-4-3zm0 2h8v4H4V2zm0 7a1.5 1.5 0 110 3 1.5 1.5 0 010-3zm8 0a1.5 1.5 0 110 3 1.5 1.5 0 010-3z" transform="scale(0.95)"/>', hue: 210 },
  grocery: { glyph: '<path d="M0 0v2h2l3 6.5L3.5 11A2 2 0 005.5 13h9v-2h-9l1-2h6a1 1 0 00.9-.5L15.5 4H4L3 2H0zm5.5 14a1.5 1.5 0 100 3 1.5 1.5 0 000-3zm7 0a1.5 1.5 0 100 3 1.5 1.5 0 000-3z" transform="scale(0.95)"/>', hue: 120 },
  default: { glyph: '<circle cx="8" cy="8" r="4" fill="#fff"/>', hue: 0 },
};

function getCategoryStyle(poiType: string, primaryHue: number): { glyph: string; hue: number } {
  const lower = (poiType || '').toLowerCase();
  for (const [key, val] of Object.entries(CATEGORY_ICONS)) {
    if (key === 'default') continue;
    if (lower.includes(key)) return val;
  }
  return { ...CATEGORY_ICONS.default, hue: (primaryHue + 180) % 360 };
}

function hueToHex(hue: number, sat = 45, light = 35): string {
  const h = hue / 360, s = sat / 100, l = light / 100;
  const hue2rgb = (p: number, q: number, t: number) => {
    if (t < 0) t += 1; if (t > 1) t -= 1;
    if (t < 1/6) return p + (q-p)*6*t;
    if (t < 1/2) return q;
    if (t < 2/3) return p + (q-p)*(2/3-t)*6;
    return p;
  };
  const q = l < 0.5 ? l*(1+s) : l+s-l*s;
  const p = 2*l - q;
  const r = Math.round(hue2rgb(p,q,h+1/3)*255);
  const g = Math.round(hue2rgb(p,q,h)*255);
  const b = Math.round(hue2rgb(p,q,h-1/3)*255);
  return `#${((1<<24)+(r<<16)+(g<<8)+b).toString(16).slice(1)}`;
}

function hexToHue(hex: string): number {
  const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!m) return 200;
  const r = parseInt(m[1],16)/255, g = parseInt(m[2],16)/255, b = parseInt(m[3],16)/255;
  const max = Math.max(r,g,b), min = Math.min(r,g,b);
  if (max === min) return 0;
  const d = max-min;
  let h = 0;
  if (max === r) h = ((g-b)/d + (g<b?6:0))/6;
  else if (max === g) h = ((b-r)/d+2)/6;
  else h = ((r-g)/d+4)/6;
  return Math.round(h*360);
}

function buildMapStyles(primaryColor: string) {
  const hue = hexToHue(primaryColor);
  return [
    { elementType: 'geometry', stylers: [{ color: '#f5f5f5' }] },
    { elementType: 'labels.icon', stylers: [{ visibility: 'off' }] },
    { elementType: 'labels.text.fill', stylers: [{ color: '#616161' }] },
    { elementType: 'labels.text.stroke', stylers: [{ color: '#f5f5f5' }] },
    { featureType: 'administrative.land_parcel', elementType: 'labels.text.fill', stylers: [{ color: '#bdbdbd' }] },
    { featureType: 'poi', elementType: 'geometry', stylers: [{ color: '#eeeeee' }] },
    { featureType: 'poi', elementType: 'labels.text.fill', stylers: [{ color: '#757575' }] },
    { featureType: 'poi.park', elementType: 'geometry', stylers: [{ color: '#e5e5e5' }] },
    { featureType: 'road', elementType: 'geometry', stylers: [{ color: '#ffffff' }] },
    { featureType: 'road.arterial', elementType: 'labels.text.fill', stylers: [{ color: '#757575' }] },
    { featureType: 'road.highway', elementType: 'geometry', stylers: [{ color: '#dadada' }] },
    { featureType: 'road.highway', elementType: 'labels.text.fill', stylers: [{ color: '#616161' }] },
    { featureType: 'transit.line', elementType: 'geometry', stylers: [{ color: '#e5e5e5' }] },
    { featureType: 'water', elementType: 'geometry', stylers: [{ color: hueToHex(hue, 15, 85) }] },
    { featureType: 'water', elementType: 'labels.text.fill', stylers: [{ color: '#9e9e9e' }] },
  ];
}

// Popup shown when the property pin is clicked. Same visual language as the
// POI popup (built via DOM nodes so text is set with textContent) but keyed on
// the property's name + full address, with a single "Get Directions" action
// that routes to the property's coordinates.
function buildPropertyInfoContent(
  name: string,
  address: string,
  lat: number,
  lng: number,
  primaryColor: string,
): HTMLElement {
  const wrap = document.createElement('div');
  wrap.style.cssText = 'font-family:var(--vibe-font-body,sans-serif);min-width:150px;max-width:230px;padding:2px 2px 4px;';

  const nameEl = document.createElement('div');
  nameEl.textContent = name || 'Property';
  nameEl.style.cssText = 'font-weight:600;font-size:14px;color:#111;line-height:1.3;';
  wrap.appendChild(nameEl);

  if (address) {
    appendPopupAddress(wrap, address);
  }

  const actions = document.createElement('div');
  actions.style.cssText = 'display:flex;gap:6px;margin-top:8px;flex-wrap:wrap;';
  const a = document.createElement('a');
  a.href = `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`;
  a.target = '_blank';
  a.rel = 'noopener noreferrer';
  a.textContent = 'Get Directions';
  a.style.cssText = `font-size:11px;font-weight:600;text-transform:uppercase;letter-spacing:0.04em;padding:6px 12px;border-radius:4px;text-decoration:none;background:${primaryColor};color:#fff;`;
  actions.appendChild(a);
  wrap.appendChild(actions);

  return wrap;
}

// Popup shown when a POI marker (or its list link) is selected. Built as DOM
// nodes so text is set via textContent (no HTML-injection surface). Mirrors the
// reference's marker popup: name, address, "Get Directions" + "Visit Website".
function buildInfoContent(poi: POI, primaryColor: string): HTMLElement {
  const wrap = document.createElement('div');
  wrap.style.cssText = 'font-family:var(--vibe-font-body,sans-serif);min-width:150px;max-width:230px;padding:2px 2px 4px;';

  const name = document.createElement('div');
  name.textContent = poi.poi_name || '';
  name.style.cssText = 'font-weight:600;font-size:14px;color:#111;line-height:1.3;';
  wrap.appendChild(name);

  if (hasVal(poi.poi_address)) {
    appendPopupAddress(wrap, poi.poi_address);
  }

  const dir = hasVal(poi.poi_directions_map_url) ? poi.poi_directions_map_url : '';
  const web = hasVal(poi.poi_business_url) ? poi.poi_business_url : '';
  if (dir || web) {
    const actions = document.createElement('div');
    actions.style.cssText = 'display:flex;flex-direction:column;gap:6px;margin-top:8px;';
    const btnBase = 'display:block;width:100%;box-sizing:border-box;text-align:center;font-size:11px;font-weight:600;text-transform:uppercase;letter-spacing:0.04em;padding:6px 12px;border-radius:4px;text-decoration:none;';
    if (dir) {
      const a = document.createElement('a');
      a.href = dir; a.target = '_blank'; a.rel = 'noopener noreferrer';
      a.textContent = 'Get Directions';
      a.style.cssText = `${btnBase}background:${primaryColor};color:#fff;`;
      actions.appendChild(a);
    }
    if (web) {
      const a = document.createElement('a');
      a.href = web; a.target = '_blank'; a.rel = 'noopener noreferrer';
      a.textContent = 'Visit Website';
      a.style.cssText = `${btnBase}border:1px solid ${primaryColor};color:#111;`;
      actions.appendChild(a);
    }
    wrap.appendChild(actions);
  }
  return wrap;
}

// ============================================================================
// Google Map component
// ============================================================================

function GoogleMap({
  propertyPins, propertyIcon,
  primaryColor, surfaceColor, pois, activeTab,
  selectedPoiIndex, onMarkerClick, onPropertyClick,
}: {
  propertyPins: PropertyPin[];
  propertyIcon: string;
  primaryColor: string;
  surfaceColor: string;
  pois: POI[];
  activeTab: string;
  selectedPoiIndex: number | null;
  onMarkerClick: (index: number) => void;
  onPropertyClick: () => void;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<any>(null);
  const markersRef = useRef<{ marker: any; normalIcon: any; highlightIcon: any; poiIndex: number }[]>([]);
  const infoWindowRef = useRef<any>(null);
  const propertyInfoWindowRef = useRef<any>(null);
  const [ready, setReady] = useState(false);
  const primaryHue = useMemo(() => hexToHue(primaryColor), [primaryColor]);
  // Stable fingerprint so we re-init the map when pin coords / labels change
  // without depending on array identity from the parent render.
  const pinsKey = propertyPins
    .map((p) => `${p.lat},${p.lng},${p.name},${p.address}`)
    .join('|');

  useEffect(() => {
    let cancelled = false;
    loadGoogleMaps().then(() => {
      if (cancelled || !containerRef.current || propertyPins.length === 0) return;
      const g = (window as any).google.maps;
      const center = { lat: propertyPins[0].lat, lng: propertyPins[0].lng };
      const map = new g.Map(containerRef.current, {
        center,
        zoom: 14,
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: false,
        zoomControl: true,
        zoomControlOptions: { position: g.ControlPosition.RIGHT_BOTTOM },
        styles: buildMapStyles(primaryColor),
      });

      infoWindowRef.current = new g.InfoWindow();
      propertyInfoWindowRef.current = new g.InfoWindow();

      const hasIcon = !!propertyIcon;
      const pinBounds = new g.LatLngBounds();

      for (const pin of propertyPins) {
        const pos = { lat: pin.lat, lng: pin.lng };
        pinBounds.extend(pos);
        const propMarker = new g.Marker({
          position: pos,
          map, title: pin.name || 'Property', zIndex: 999,
          icon: {
            url: makePropertyPinBg(surfaceColor, primaryColor, hasIcon),
            scaledSize: new g.Size(60, 74),
            anchor: new g.Point(30, 74),
          },
        });
        // Clicking a property pin opens its own InfoWindow (name + address +
        // "Get Directions"). It also deselects any active POI so the POI-sync
        // effect closes that InfoWindow, ensuring only one popup is visible at
        // a time.
        propMarker.addListener('click', () => {
          onPropertyClick();
          infoWindowRef.current?.close();
          propertyInfoWindowRef.current.setContent(
            buildPropertyInfoContent(pin.name, pin.address, pin.lat, pin.lng, primaryColor),
          );
          propertyInfoWindowRef.current.open(map, propMarker);
        });
        if (hasIcon) {
          new g.Marker({
            position: pos,
            map, zIndex: 1000, clickable: false,
            icon: {
              url: propertyIcon,
              scaledSize: new g.Size(24, 24),
              anchor: new g.Point(12, 59),
            },
          });
        }
      }

      // Two communities sit ~3 miles apart — fit both pins on first load so
      // neither is clipped off the initial viewport.
      if (propertyPins.length > 1) {
        map.fitBounds(pinBounds, { top: 60, right: 60, bottom: 60, left: 60 });
      }

      mapRef.current = map;
      setReady(true);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [pinsKey, propertyPins, primaryColor, surfaceColor, propertyIcon, onPropertyClick]);

  const syncMarkers = useCallback(() => {
    const map = mapRef.current;
    const g = (window as any).google?.maps;
    if (!map || !g) return;

    markersRef.current.forEach((m) => m.marker.setMap(null));
    markersRef.current = [];

    const visible = activeTab === 'all'
      ? pois.map((p, i) => ({ poi: p, origIndex: i }))
      : pois.map((p, i) => ({ poi: p, origIndex: i })).filter(({ poi }) => (poi.poi_type || 'Nearby') === activeTab);

    const bounds = new g.LatLngBounds();
    for (const pin of propertyPins) {
      bounds.extend({ lat: pin.lat, lng: pin.lng });
    }
    let hasPoiBounds = false;
    const iconCache = new Map<string, { normal: any; highlight: any }>();

    for (const { poi, origIndex } of visible) {
      const lat = parseFloat(poi.poi_latitude || '');
      const lng = parseFloat(poi.poi_longitude || '');
      if (isNaN(lat) || isNaN(lng)) continue;

      const pos = { lat, lng };
      bounds.extend(pos);
      hasPoiBounds = true;

      const cat = poi.poi_type || 'Nearby';
      if (!iconCache.has(cat)) {
        const style = getCategoryStyle(cat, primaryHue);
        iconCache.set(cat, {
          normal: { url: makeSvgMarker('#111111', style.glyph, 28), scaledSize: new g.Size(28, 36), anchor: new g.Point(14, 36) },
          highlight: { url: makeHighlightMarker(primaryColor, style.glyph, 40), scaledSize: new g.Size(40, 54), anchor: new g.Point(20, 54) },
        });
      }

      const icons = iconCache.get(cat)!;
      const isSelected = selectedPoiIndex === origIndex;

      const marker = new g.Marker({
        position: pos, map,
        title: poi.poi_name || '',
        icon: isSelected ? icons.highlight : icons.normal,
        zIndex: isSelected ? 900 : 1,
        animation: isSelected ? g.Animation.BOUNCE : null,
      });

      if (isSelected) setTimeout(() => marker.setAnimation(null), 1400);

      marker.addListener('click', () => onMarkerClick(origIndex));

      markersRef.current.push({ marker, normalIcon: icons.normal, highlightIcon: icons.highlight, poiIndex: origIndex });
    }

    // Fit to POIs + both property pins whenever POIs are visible; when the
    // category is empty, still frame the dual community pins.
    if (hasPoiBounds || propertyPins.length > 1) {
      map.fitBounds(bounds, { top: 50, right: 50, bottom: 50, left: 50 });
      const listener = g.event.addListener(map, 'idle', () => {
        if (map.getZoom() > 15) map.setZoom(15);
        g.event.removeListener(listener);
      });
    }
  }, [pois, activeTab, propertyPins, primaryHue, primaryColor, selectedPoiIndex, onMarkerClick]);

  useEffect(() => { if (ready) syncMarkers(); }, [ready, syncMarkers]);

  // When selected POI changes, update marker icons, pan, and open a popup.
  useEffect(() => {
    const g = (window as any).google?.maps;
    const map = mapRef.current;
    const iw = infoWindowRef.current;
    if (!g || !map || !ready) return;

    let selected: { marker: any; highlightIcon: any; normalIcon: any; poiIndex: number } | null = null;
    for (const entry of markersRef.current) {
      const isSelected = entry.poiIndex === selectedPoiIndex;
      entry.marker.setIcon(isSelected ? entry.highlightIcon : entry.normalIcon);
      entry.marker.setZIndex(isSelected ? 900 : 1);
      if (isSelected) selected = entry;
    }

    if (selected && selectedPoiIndex !== null) {
      // Selecting a POI supersedes the property popup — close it so we never
      // have two InfoWindows visible at once.
      propertyInfoWindowRef.current?.close();
      const marker = selected.marker;
      marker.setAnimation(g.Animation.BOUNCE);
      setTimeout(() => marker.setAnimation(null), 1400);
      map.panTo(marker.getPosition());
      const poi = pois[selectedPoiIndex];
      if (iw && poi) {
        iw.setContent(buildInfoContent(poi, primaryColor));
        iw.open(map, marker);
      }
    } else if (iw) {
      iw.close();
    }
  }, [selectedPoiIndex, ready, pois, primaryColor]);

  return (
    <div className="w-full h-full relative rounded overflow-hidden">
      <div ref={containerRef} className="w-full h-full" />
      {!ready && (
        <div className="absolute inset-0 flex items-center justify-center bg-[#f5f5f5]">
          <span className="font-body text-sm text-[var(--vibe-color-text-muted)] animate-pulse">Loading map…</span>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// Main component
// ============================================================================

export function NeighborhoodMap() {
  const property = useProperty();
  const pois = useCollectionBinding<POI>('neighborhoodmap.pois', { collection: 'neighborhood_pois' });
  const foxRidgePin = useSlotGroup('neighborhoodmap.fox_ridge_pin', foxRidgePinDef);
  const lakeviewParkPin = useSlotGroup('neighborhoodmap.lakeview_park_pin', lakeviewParkPinDef);

  const propertyIcon = (property?.property_icon as string) || '';
  const primaryColor = (property?.property_primary_color as string) || '#4a7c59';
  // Surface color is used for the resting POI pin fill (selected pins still
  // switch to primary). Falls back to the default surface token (#f5f5f5) when
  // the property hasn't set a custom surface color.
  const surfaceColor = (property?.property_color_3 as string) || '#f5f5f5';

  const allPois = pois && pois.length > 0 ? pois : [];

  const propertyPins = useMemo(() => {
    const pins: PropertyPin[] = [];
    const foxLat = parseFloat(foxRidgePin.latitude || '');
    const foxLng = parseFloat(foxRidgePin.longitude || '');
    if (!isNaN(foxLat) && !isNaN(foxLng)) {
      pins.push({
        name: foxRidgePin.name || 'Fox Ridge Apartments',
        address: foxRidgePin.address || '817 Walden Ct, Limerick, PA 19468',
        lat: foxLat,
        lng: foxLng,
      });
    }
    const lakeLat = parseFloat(lakeviewParkPin.latitude || '');
    const lakeLng = parseFloat(lakeviewParkPin.longitude || '');
    if (!isNaN(lakeLat) && !isNaN(lakeLng)) {
      pins.push({
        name: lakeviewParkPin.name || 'Lakeview Park Apartments',
        address: lakeviewParkPin.address || '601 Lakeview Dr, Royersford, PA 19468',
        lat: lakeLat,
        lng: lakeLng,
      });
    }
    return pins;
  }, [foxRidgePin, lakeviewParkPin]);

  const categories = useMemo(() => {
    const cats = new Set<string>();
    for (const poi of allPois) cats.add(poi.poi_type || 'Nearby');
    return Array.from(cats);
  }, [allPois]);

  // Aion Management: the "All" pill is intentionally hidden — only per-category
  // pills render. So the initial active tab must be a real category (the first
  // one) rather than 'all', otherwise the POI list + map render every POI with
  // no visible filter pill selected.
  const [activeTab, setActiveTab] = useState<string>(() => categories[0] || '');
  const [selectedPoiIndex, setSelectedPoiIndex] = useState<number | null>(null);
  const [expanded, setExpanded] = useState(false);
  const mapRef = useRef<HTMLDivElement>(null);

  // POIs are fetched at runtime, so on the first render `categories` may still
  // be empty. Once they arrive (or the property changes), snap the active tab
  // to the first real category if we don't yet have one — or if the previously
  // selected category disappeared.
  useEffect(() => {
    if (categories.length === 0) return;
    if (!activeTab || !categories.includes(activeTab)) {
      setActiveTab(categories[0]);
      setSelectedPoiIndex(null);
      setExpanded(false);
    }
  }, [categories, activeTab]);

  // Show a compact slice of long lists by default (esp. the "All" tab), with
  // "Show all" revealing the rest. Desktop shows 4 rows of the grid (16); on
  // tablet the grid drops to 2 columns, so cap at 2 × 4 = 8 to keep the same
  // tidy 4-row block. On mobile the grid is a single column, so cap at 5 rows
  // to keep the default list short with "Show all" sitting just below it.
  const [poiLimit, setPoiLimit] = useState(16);
  useEffect(() => {
    const mobileMq = window.matchMedia('(max-width: 767px)');
    const tabletMq = window.matchMedia('(min-width: 768px) and (max-width: 1023px)');
    const update = () => setPoiLimit(mobileMq.matches ? 5 : tabletMq.matches ? 8 : 16);
    update();
    mobileMq.addEventListener('change', update);
    tabletMq.addEventListener('change', update);
    return () => {
      mobileMq.removeEventListener('change', update);
      tabletMq.removeEventListener('change', update);
    };
  }, []);

  const handleMarkerClick = useCallback((origIndex: number) => {
    setSelectedPoiIndex(origIndex);
  }, []);

  // Clicking a property pin clears any active POI so the POI-sync effect
  // tears down the POI InfoWindow — property pins open their own popup and
  // we only ever want one visible at a time.
  const handlePropertyClick = useCallback(() => {
    setSelectedPoiIndex(null);
  }, []);

  const handleTabChange = useCallback((tab: string) => {
    setActiveTab(tab);
    setSelectedPoiIndex(null);
    setExpanded(false);
  }, []);

  // A POI link may sit in a category the map is currently filtered away from;
  // switch to that POI's own category so its marker is visible, then select +
  // scroll to the map. (There is no "All" pill in this theme, so we can't fall
  // back to that.)
  const handlePoiLink = useCallback((poi: POI, origIndex: number) => {
    const cat = poi.poi_type || 'Nearby';
    if (activeTab !== cat) setActiveTab(cat);
    setSelectedPoiIndex(origIndex);
    mapRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, [activeTab]);

  const hasCoords = propertyPins.length > 0;

  // Aion Management: the "All" pill is intentionally hidden — only per-category
  // pills render, so a real category key is always active. Per-category tabs
  // never repeat a place, so no dedup pass is needed here (unlike an aggregated
  // "All" view where a place tagged under multiple categories would collide).
  const allTabs = categories.map((c) => ({ key: c, label: c }));
  const poisFor = (key: string) =>
    allPois
      .map((poi, origIndex) => ({ poi, origIndex }))
      .filter(({ poi }) => (poi.poi_type || 'Nearby') === key);

  return (
    <section className="py-8 md:py-12 bg-white">
      {/* Full-width map on top — matches the reference (map, then tabs, then
          columns of POI links below). */}
      <div ref={mapRef} className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="nbm-map">
          {hasCoords ? (
            <GoogleMap
              propertyPins={propertyPins}
              propertyIcon={propertyIcon} primaryColor={primaryColor}
              surfaceColor={surfaceColor}
              pois={allPois} activeTab={activeTab}
              selectedPoiIndex={selectedPoiIndex}
              onMarkerClick={handleMarkerClick}
              onPropertyClick={handlePropertyClick}
            />
          ) : (
            <div className="w-full h-full bg-[#f5f5f5] flex items-center justify-center text-[var(--vibe-color-text-muted)]">
              <p className="font-body">Map coordinates not available</p>
            </div>
          )}
        </div>

        {/* Dual-community legend — doubles as the editable hit target for each
            property pin's name/address/coords in the inline editor. */}
        <div className="nbm-pin-legend" aria-label="Community locations on the map">
          <EditableSlotGroup groupPath="neighborhoodmap.fox_ridge_pin" definition={foxRidgePinDef}>
            <span className="nbm-pin-legend-item">
              <span className="nbm-pin-legend-dot" aria-hidden="true" />
              {foxRidgePin.name || 'Fox Ridge Apartments'}
            </span>
          </EditableSlotGroup>
          <EditableSlotGroup groupPath="neighborhoodmap.lakeview_park_pin" definition={lakeviewParkPinDef}>
            <span className="nbm-pin-legend-item">
              <span className="nbm-pin-legend-dot" aria-hidden="true" />
              {lakeviewParkPin.name || 'Lakeview Park Apartments'}
            </span>
          </EditableSlotGroup>
        </div>
      </div>

      {categories.length > 0 && (() => {
        // Order longest names first so the two-line entries cluster in the top
        // rows and the tidy single-line ones fill in below. Length is a
        // deterministic proxy for "wraps to two lines" — unlike DOM measurement
        // it stays identical on server + client, so there's no hydration reflow.
        // (Array.sort is stable, so equal-length names keep their source order.)
        const activeItems = [...poisFor(activeTab)].sort(
          (a, b) => (b.poi.poi_name?.length || 0) - (a.poi.poi_name?.length || 0),
        );
        const visibleItems = expanded ? activeItems : activeItems.slice(0, poiLimit);
        const hiddenCount = activeItems.length - visibleItems.length;
        return (
          <EditableCollectionBinding bindingPath="neighborhoodmap.pois" label="Points of Interest">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 mt-8 md:mt-10">
            {/* Category filter pills — size to their labels (no truncation),
                wrap to as many rows as needed, each with a POI count. */}
            <div className="nbm-tabrow" role="tablist" aria-label="Points of interest categories">
              {allTabs.map((tab) => {
                const count = poisFor(tab.key).length;
                return (
                  <button
                    key={tab.key}
                    role="tab"
                    aria-selected={activeTab === tab.key}
                    className={`nbm-tab${activeTab === tab.key ? ' nbm-tab--active' : ''}`}
                    onClick={() => handleTabChange(tab.key)}
                  >
                    <span className="nbm-tab-label">{tab.label}</span>
                    <span className="nbm-tab-count">{count}</span>
                  </button>
                );
              })}
            </div>

            {/* Only the active category renders — as a responsive grid that
                fills the available width. Names wrap to two lines instead of
                being clipped, so full business names stay readable. */}
            {activeItems.length > 0 ? (
              <>
                <ul className="nbm-grid">
                  {visibleItems.map(({ poi, origIndex }) => (
                    <li key={origIndex}>
                      <button
                        className={`nbm-poi-link${selectedPoiIndex === origIndex ? ' nbm-poi-link--active' : ''}`}
                        onClick={() => handlePoiLink(poi, origIndex)}
                      >
                        {(() => {
                          const words = (poi.poi_name || '').trim().split(/\s+/);
                          const last = words.pop() || '';
                          const head = words.join(' ');
                          return (
                            <span className="nbm-poi-name">
                              {head ? head + ' ' : ''}
                              {/* Last word + arrow stay on one line so the arrow
                                  never wraps onto a line by itself. */}
                              <span className="nbm-poi-tail">
                                {last}
                                <svg className="nbm-arrow" width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
                                  <path d="M4.5 9.5l5-5M5 4.5h4.5V9" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                              </span>
                            </span>
                          );
                        })()}
                      </button>
                    </li>
                  ))}
                </ul>
                {activeItems.length > poiLimit && (
                  <div className="nbm-more-wrap">
                    <button className="nbm-more" onClick={() => setExpanded((v) => !v)}>
                      {expanded ? 'Show fewer' : `Show all ${activeItems.length}`}
                      {!expanded && hiddenCount > 0 ? ` (+${hiddenCount})` : ''}
                    </button>
                  </div>
                )}
              </>
            ) : (
              <p className="nbm-empty">No locations in this category.</p>
            )}
          </div>
          </EditableCollectionBinding>
        );
      })()}

      <style>{`
        /* ===== Map (full-width, prominent — sits on top) ===== */
        .nbm-map {
          width: 100%;
          height: 380px;
          border-radius: 8px;
          overflow: hidden;
          position: relative;
        }
        @media (min-width: 768px) { .nbm-map { height: 480px; } }
        @media (min-width: 1024px) { .nbm-map { height: 540px; } }

        /* ===== Dual-community pin legend ===== */
        .nbm-pin-legend {
          display: flex;
          flex-wrap: wrap;
          justify-content: center;
          gap: 0.75rem 1.75rem;
          margin-top: 1rem;
        }
        .nbm-pin-legend-item {
          display: inline-flex;
          align-items: center;
          gap: 0.45rem;
          font-family: var(--vibe-font-body);
          font-size: 0.875rem;
          color: var(--vibe-color-text);
        }
        .nbm-pin-legend-dot {
          width: 0.65rem;
          height: 0.65rem;
          border-radius: 9999px;
          background: var(--vibe-color-surface, #f5f5f5);
          border: 2px solid color-mix(in srgb, var(--vibe-color-text) 35%, transparent);
          flex-shrink: 0;
        }

        /* ===== Category filter pills (wrap, size to label — no truncation) ===== */
        .nbm-tabrow {
          display: flex;
          flex-wrap: wrap;
          justify-content: center;
          gap: 0.5rem;
        }
        .nbm-tab {
          appearance: none;
          display: inline-flex;
          align-items: center;
          gap: 0.45rem;
          background: color-mix(in srgb, var(--vibe-color-text-muted) 8%, transparent);
          border: 1px solid var(--vibe-color-border);
          border-radius: 9999px;
          padding: 0.5rem 0.95rem;
          font-family: var(--vibe-font-heading, var(--vibe-font-body));
          font-size: 0.8rem;
          font-weight: 600;
          letter-spacing: 0.04em;
          text-transform: uppercase;
          color: var(--vibe-color-text-muted);
          cursor: pointer;
          white-space: nowrap;
          transition: color 0.2s, background 0.2s, border-color 0.2s;
        }
        @media (min-width: 768px) { .nbm-tab { font-size: 0.85rem; } }
        .nbm-tab:hover {
          color: var(--vibe-color-primary);
          border-color: var(--vibe-color-primary);
          background: color-mix(in srgb, var(--vibe-color-primary) 8%, transparent);
        }
        /* Active pill: solid, slightly-darkened primary with explicit white
           text. The theme's auto-computed --vibe-color-text-on-primary flips to
           dark on lighter primaries (e.g. this gold), which reads low-contrast
           on the pill — darkening the fill keeps white legible everywhere. */
        .nbm-tab--active,
        .nbm-tab--active:hover {
          background: color-mix(in srgb, var(--vibe-color-primary) 90%, #000);
          border-color: color-mix(in srgb, var(--vibe-color-primary) 90%, #000);
          color: #fff;
        }
        .nbm-tab-count {
          font-size: 0.72rem;
          font-weight: 700;
          line-height: 1;
          padding: 0.15em 0.5em;
          border-radius: 9999px;
          background: color-mix(in srgb, currentColor 15%, transparent);
        }

        /* ===== Active-category POI grid (fills width; names wrap 2 lines) ===== */
        .nbm-grid {
          list-style: none;
          margin: 1.75rem 0 0;
          padding: 0;
          display: grid;
          /* Responsive, but never more than 4 columns across: each column is at
             least 230px, and at least a quarter of the row (minus the 3 gaps). */
          grid-template-columns: repeat(auto-fill, minmax(max(230px, calc((100% - 3 * 1.75rem) / 4)), 1fr));
          gap: 0.15rem 1.75rem;
        }
        /* Tablet: pin to exactly 2 columns so the default slice (8 POIs) lays
           out as a tidy 2 × 4 block, with "Show all" sitting below it. */
        @media (min-width: 768px) and (max-width: 1023px) {
          .nbm-grid { grid-template-columns: repeat(2, 1fr); }
        }
        .nbm-poi-link {
          display: block;
          width: 100%;
          padding: 0.45rem 0;
          background: transparent;
          border: none;
          font-family: var(--vibe-font-body);
          font-size: 1rem;
          line-height: 1.35;
          text-align: center;
          color: var(--vibe-color-text);
          cursor: pointer;
          transition: color 0.15s;
        }
        .nbm-poi-link:hover,
        .nbm-poi-link--active { color: var(--vibe-color-primary); }
        /* Arrow lives inline at the end of the name, so it always trails the
           last word with a fixed gap — regardless of one- or two-line names. */
        .nbm-poi-name {
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
        .nbm-poi-tail { white-space: nowrap; }
        .nbm-arrow {
          display: inline-block;
          vertical-align: middle;
          margin-left: 0.35rem;
          color: currentColor;
          transition: transform 0.15s;
        }
        .nbm-poi-link:hover .nbm-arrow { transform: translate(2px, -2px); }

        /* ===== "Show all" expander + empty state ===== */
        .nbm-more-wrap { display: flex; justify-content: center; margin-top: 1.5rem; padding-bottom: 20px; }
        .nbm-more {
          appearance: none;
          background: transparent;
          border: 1px solid var(--vibe-color-border);
          border-radius: 9999px;
          padding: 0.55rem 1.5rem;
          font-family: var(--vibe-font-heading, var(--vibe-font-body));
          font-size: 0.8rem;
          font-weight: 600;
          letter-spacing: 0.06em;
          text-transform: uppercase;
          color: var(--vibe-color-primary);
          cursor: pointer;
          transition: background 0.2s, border-color 0.2s;
        }
        .nbm-more:hover {
          background: color-mix(in srgb, var(--vibe-color-primary) 10%, transparent);
          border-color: var(--vibe-color-primary);
        }
        .nbm-empty {
          margin-top: 1.75rem;
          text-align: center;
          font-family: var(--vibe-font-body);
          font-size: 0.9rem;
          color: var(--vibe-color-text-muted);
        }

        /* ===== Mobile: shrink the category pills + POI list a touch. POI
           links are already center-aligned at every breakpoint (see the base
           .nbm-poi-link rule above). ===== */
        @media (max-width: 767px) {
          .nbm-tab {
            font-size: 0.72rem;
            padding: 0.4rem 0.8rem;
          }
          .nbm-tab-count { font-size: 0.66rem; }
          .nbm-poi-link { font-size: 0.9rem; }
          /* At single-column the full row width is available, so drop the
             2-line clamp to let long names breathe onto a third line if needed. */
          .nbm-poi-name {
            display: block;
            overflow: visible;
          }
        }

        @media (prefers-reduced-motion: reduce) {
          .nbm-poi-link, .nbm-arrow { transition: none; }
        }

        /* ===== Google Maps info window cleanup ===== */
        .gm-style .gm-style-iw-c { border-radius: 6px !important; box-shadow: 0 4px 20px rgba(0,0,0,0.12) !important; padding: 8px 12px !important; }
        .gm-style .gm-style-iw-d { overflow: hidden !important; }
        .gm-style .gm-style-iw-tc::after { background: white !important; }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'NeighborhoodMap',
  displayName: 'Neighborhood Map',
  tokens: {},
  slotGroups: {
    'neighborhoodmap.fox_ridge_pin': foxRidgePinDef,
    'neighborhoodmap.lakeview_park_pin': lakeviewParkPinDef,
  },
  collectionBindings: {
    'neighborhoodmap.pois': {
      label: 'Points of Interest',
      collection: 'neighborhood_pois',
    },
  },
};

(NeighborhoodMap as any).contentSchema = contentSchema;

export default NeighborhoodMap;
